/**
 * MostLoggedRoutes Component
 *
 * Displays the top 10 most logged routes (by sends + attempts).
 * Features podium-style highlighting for top 3 routes.
 */

import { type CSSProperties } from 'react';
import type { VGrade } from '@flash/shared';

/**
 * Route with stats for analytics display
 */
interface RouteWithStats {
  id: string;
  name: string;
  officialGrade: VGrade;
  totalSends: number;
  totalAttempts: number;
  averageRating: number | null;
  ratingCount: number;
  setterName: string;
}

/**
 * Props for MostLoggedRoutes component
 */
interface MostLoggedRoutesProps {
  /** Array of routes sorted by engagement (sends + attempts) */
  routes: RouteWithStats[];
}

/**
 * Get podium badge color based on rank
 */
const getPodiumColor = (rank: number): string | null => {
  switch (rank) {
    case 1:
      return '#fbbf24'; // Gold
    case 2:
      return '#9ca3af'; // Silver
    case 3:
      return '#d97706'; // Bronze
    default:
      return null;
  }
};

/**
 * MostLoggedRoutes displays the most engaged routes at the gym
 *
 * @example
 * ```tsx
 * <MostLoggedRoutes routes={analytics.mostLoggedRoutes} />
 * ```
 */
export const MostLoggedRoutes = ({ routes }: MostLoggedRoutesProps) => {
  if (routes.length === 0) {
    return (
      <div style={containerStyle}>
        <h3 style={titleStyle}>Most Logged Routes</h3>
        <div style={emptyStateStyle}>
          <p style={emptyTextStyle}>No climb logs yet.</p>
          <p style={emptySubtextStyle}>Routes will appear here once climbers start logging.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h3 style={titleStyle}>Most Logged Routes</h3>
      <p style={subtitleStyle}>Top 10 routes by total sends and attempts</p>

      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Rank</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Route</th>
              <th style={thStyle}>Grade</th>
              <th style={thStyle}>Sends</th>
              <th style={thStyle}>Attempts</th>
              <th style={thStyle}>Total</th>
            </tr>
          </thead>
          <tbody>
            {routes.map((route, index) => {
              const rank = index + 1;
              const podiumColor = getPodiumColor(rank);
              const totalEngagement = route.totalSends + route.totalAttempts;

              return (
                <tr key={route.id} style={rank <= 3 ? highlightedRowStyle : rowStyle}>
                  <td style={tdCenterStyle}>
                    {podiumColor ? (
                      <span style={{ ...podiumBadgeStyle, backgroundColor: podiumColor }}>
                        {rank}
                      </span>
                    ) : (
                      <span style={rankStyle}>{rank}</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={routeNameStyle}>{route.name}</span>
                    <span style={setterNameStyle}>by {route.setterName}</span>
                  </td>
                  <td style={tdCenterStyle}>
                    <span style={gradeBadgeStyle}>{route.officialGrade}</span>
                  </td>
                  <td style={tdCenterStyle}>
                    <span style={sendCountStyle}>{route.totalSends}</span>
                  </td>
                  <td style={tdCenterStyle}>
                    <span style={attemptCountStyle}>{route.totalAttempts}</span>
                  </td>
                  <td style={tdCenterStyle}>
                    <span style={totalCountStyle}>{totalEngagement}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#1f2937',
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: '#6b7280',
};

const emptyStateStyle: CSSProperties = {
  padding: '32px',
  textAlign: 'center',
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  border: '1px dashed #d1d5db',
};

const emptyTextStyle: CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: '0.875rem',
  color: '#374151',
  fontWeight: 500,
};

const emptySubtextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.8125rem',
  color: '#6b7280',
};

const tableContainerStyle: CSSProperties = {
  overflowX: 'auto',
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
};

const thStyle: CSSProperties = {
  padding: '12px 16px',
  borderBottom: '2px solid #e5e7eb',
  textAlign: 'center',
  fontWeight: 600,
  color: '#374151',
  whiteSpace: 'nowrap',
};

const rowStyle: CSSProperties = {
  borderBottom: '1px solid #f3f4f6',
};

const highlightedRowStyle: CSSProperties = {
  ...rowStyle,
  backgroundColor: '#fffbeb',
};

const tdStyle: CSSProperties = {
  padding: '14px 16px',
  verticalAlign: 'middle',
};

const tdCenterStyle: CSSProperties = {
  ...tdStyle,
  textAlign: 'center',
};

const podiumBadgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  color: 'white',
  fontWeight: 700,
  fontSize: '0.875rem',
};

const rankStyle: CSSProperties = {
  color: '#6b7280',
  fontWeight: 500,
};

const routeNameStyle: CSSProperties = {
  color: '#1f2937',
  fontWeight: 500,
  display: 'block',
};

const setterNameStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.75rem',
  color: '#9ca3af',
  marginTop: '2px',
};

const gradeBadgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '4px 10px',
  backgroundColor: '#e5e7eb',
  borderRadius: '12px',
  fontWeight: 600,
  color: '#374151',
  fontSize: '0.8125rem',
};

const sendCountStyle: CSSProperties = {
  color: '#059669',
  fontWeight: 600,
};

const attemptCountStyle: CSSProperties = {
  color: '#6b7280',
};

const totalCountStyle: CSSProperties = {
  fontWeight: 700,
  color: '#1f2937',
};

export default MostLoggedRoutes;
